/**
 * One Euro Filter — Adaptive low-pass filter for noisy signals.
 *
 * Designed specifically for human input (gaze, hand, touch tracking).
 * Smooths when still, responsive when moving.
 *
 * Paper: "1€ Filter: A Simple Speed-based Low-pass Filter for Noisy Input"
 * — Géry Casiez, Nicolas Roussel, Daniel Vogel (CHI 2012)
 *
 * Parameters:
 *   minCutoff: minimum cutoff frequency (Hz). Lower = smoother when still, but laggier.
 *   beta: speed coefficient. Higher = more responsive when moving fast.
 *   dCutoff: cutoff for derivative estimation. Rarely needs tuning.
 */

class LowPassFilter {
  private y: number | null = null
  private s: number | null = null

  alpha(cutoff: number, dt: number): number {
    const tau = 1.0 / (2 * Math.PI * cutoff)
    return 1.0 / (1.0 + tau / dt)
  }

  filter(value: number, dt: number, cutoff: number): number {
    const a = this.alpha(cutoff, dt)
    if (this.y === null) {
      this.y = value
      this.s = value
    } else {
      this.s = a * value + (1 - a) * this.s!
      this.y = value
    }
    return this.s!
  }

  lastValue(): number | null {
    return this.s
  }

  reset() {
    this.y = null
    this.s = null
  }
}

export class OneEuroFilter {
  private minCutoff: number
  private beta: number
  private dCutoff: number
  private xFilter = new LowPassFilter()
  private dxFilter = new LowPassFilter()
  private lastTime: number | null = null

  constructor(minCutoff = 1.0, beta = 0.7, dCutoff = 1.0) {
    this.minCutoff = minCutoff
    this.beta = beta
    this.dCutoff = dCutoff
  }

  filter(value: number, timestamp: number): number {
    if (this.lastTime === null) {
      this.lastTime = timestamp
      return this.xFilter.filter(value, 1 / 60, this.minCutoff)
    }

    const dt = Math.max((timestamp - this.lastTime) / 1000, 1e-6) // seconds
    this.lastTime = timestamp

    // Estimate derivative (speed)
    const prevFiltered = this.xFilter.lastValue()
    const dx = prevFiltered !== null ? (value - prevFiltered) / dt : 0
    const edx = this.dxFilter.filter(dx, dt, this.dCutoff)

    // Adaptive cutoff: fast movement → higher cutoff → less smoothing
    const cutoff = this.minCutoff + this.beta * Math.abs(edx)

    return this.xFilter.filter(value, dt, cutoff)
  }

  reset() {
    this.xFilter.reset()
    this.dxFilter.reset()
    this.lastTime = null
  }
}

/**
 * Convenience: 2D One Euro Filter for gaze coordinates.
 */
export class OneEuroFilter2D {
  private xFilter: OneEuroFilter
  private yFilter: OneEuroFilter

  constructor(minCutoff = 1.0, beta = 0.7, dCutoff = 1.0) {
    this.xFilter = new OneEuroFilter(minCutoff, beta, dCutoff)
    this.yFilter = new OneEuroFilter(minCutoff, beta, dCutoff)
  }

  filter(x: number, y: number, timestamp: number): { x: number; y: number } {
    return {
      x: this.xFilter.filter(x, timestamp),
      y: this.yFilter.filter(y, timestamp),
    }
  }

  reset() {
    this.xFilter.reset()
    this.yFilter.reset()
  }
}
